import {BoundaryCollection} from '../../../../../core/model/modflow/boundaries';
import {Button, Grid, Menu, Segment} from 'semantic-ui-react';
import {EMessageState, IMessage} from '../../../../../core/model/messages/Message.type';
import {IRootReducer} from '../../../../../reducers';
import {ISubstance} from '../../../../../core/model/modflow/transport/Substance.type';
import {ModflowModel} from '../../../../../core/model/modflow';
import {Substance, Transport as TransportAlias} from '../../../../../core/model/modflow/transport';
import {addMessage, removeMessage, updateMessage, updateTransport} from '../../../actions/actions';
import {messageDirty, messageError, messageSaving} from '../../../defaults/messages';
import {sendCommand} from '../../../../../services/api';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import Command from '../../../commands/modflowModelCommand';
import ContentToolBar from '../../../../shared/ContentToolbar2';
import MessagesCollection from '../../../../../core/model/messages/MessagesCollection';
import React, {useEffect, useRef, useState} from 'react';
import SubstanceDetails from './SubstanceDetails';
import SubstanceList from './SubstanceList';

const Transport = () => {
    const [selectedSubstance, setSelectedSubstance] = useState<ISubstance | null>(null);

    const T03 = useSelector((state: IRootReducer) => state.T03);
    const model = T03.model ? ModflowModel.fromObject(T03.model) : null;
    const boundaries = T03.boundaries ? BoundaryCollection.fromObject(T03.boundaries) : null;
    const transportInstance = T03.transport ? TransportAlias.fromObject(T03.transport) : null;
    const messages = MessagesCollection.fromObject(T03.messages);

    const dispatch = useDispatch();
    const {property} = useParams<{property: string}>();

    const transportRef = useRef<TransportAlias>();
    const editingState = useRef<{ [key: string]: IMessage | null }>({
        dirty: null,
        saving: null
    });

    useEffect(() => {
        if (!selectedSubstance && transportInstance && transportInstance.substances.length > 0) {
            handleSubstanceListClick(transportInstance.substances.first.id);
        }
        return function cleanup() {
            handleSave();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        editingState.current = messages.getEditingState(property);
        if (transportInstance) {
            transportRef.current = transportInstance;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, transportInstance]);

    useEffect(() => {
        if (!selectedSubstance && transportInstance && transportInstance.substances.length > 0) {
            handleSubstanceListClick(transportInstance.substances.first.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transportInstance]);

    if (!boundaries || !model || !transportInstance) {
        return (
            <Segment color={'grey'} loading={true}/>
        );
    }

    const handleAddSubstance = () => {
        const substance = Substance.create('new substance');
        const cTransport = transportInstance;
        cTransport.addSubstance(substance);
        cTransport.enabled = true;
        dispatch(updateTransport(cTransport));
        setSelectedSubstance(substance);
    };

    const handleSubstanceListClick = (id: string) => {
        const substance = transportInstance.substances.findById(id);
        setSelectedSubstance(substance || null);
    };

    const handleRemoveSubstance = (id: string) => {
        const cTransport = transportInstance;
        cTransport.removeSubstanceById(id);
        dispatch(updateTransport(cTransport));
        if (!editingState.current.dirty) {
            dispatch(addMessage(messageDirty(property)));
        }

        if (cTransport.substances.length === 0) {
            return setSelectedSubstance(null);
        }
        setSelectedSubstance(cTransport.substances.first);
    };

    const handleChangeSubstance = (substance: Substance) => {
        const cTransport = transportInstance;
        cTransport.updateSubstance(substance);
        dispatch(updateTransport(cTransport));
        if (!editingState.current.dirty) {
            dispatch(addMessage(messageDirty(property)));
        }
        setSelectedSubstance(substance.toObject());
    };

    const handleToggleEnabled = () => {
        const cTransport = transportInstance;
        cTransport.enabled = !cTransport.enabled;
        dispatch(updateTransport(cTransport));
        if (!editingState.current.dirty) {
            dispatch(addMessage(messageDirty(property)));
        }
    };

    const handleSave = () => {
        if (!editingState.current.dirty || !transportRef.current) {
            return null;
        }
        const filteredSubstances = transportRef.current.substances.all.filter(
            (s) => s.boundaryConcentrations.length === 0);
        if (filteredSubstances.length > 0) {
            dispatch(addMessage(messageError('transport', 'Substances must be applied to at least one boundary.')));
            return null;
        }
        const message = messageSaving(property);
        dispatch(addMessage(message));
        return sendCommand(
            Command.updateTransport({
                id: model.id,
                transport: transportRef.current.toObject(),
            }), () => {
                if (editingState.current.dirty) {
                    dispatch(removeMessage(editingState.current.dirty));
                }
                return dispatch(updateMessage({...message, state: EMessageState.SUCCESS}));
            }, (e) => dispatch(addMessage(messageError(property, e)))
        );
    };

    return (
        <Segment color={'grey'}>
            <Grid>
                <Grid.Row>
                    <Grid.Column width={4}>
                        <Menu fluid={true} vertical={true} tabular={true}>
                            <Menu.Item>
                                <Button
                                    disabled={model.readOnly}
                                    negative={!transportInstance.enabled}
                                    positive={transportInstance.enabled}
                                    icon={transportInstance.enabled ? 'toggle on' : 'toggle off'}
                                    labelPosition="left"
                                    onClick={handleToggleEnabled}
                                    content={transportInstance.enabled ? 'Enabled' : 'Disabled'}
                                    style={{marginLeft: '-20px', width: '200px'}}
                                />
                            </Menu.Item>
                            <SubstanceList
                                addSubstance={handleAddSubstance}
                                onClick={handleSubstanceListClick}
                                onRemove={handleRemoveSubstance}
                                selected={selectedSubstance ? selectedSubstance.id : undefined}
                                substances={transportInstance.substances}
                                readOnly={model.readOnly}
                            />
                        </Menu>
                    </Grid.Column>
                    <Grid.Column width={12}>
                        <div>
                            <ContentToolBar
                                buttonSave={true}
                                onSave={handleSave}
                            />
                            <SubstanceDetails
                                substance={selectedSubstance ? Substance.fromObject(selectedSubstance) : undefined}
                                boundaries={boundaries}
                                onChange={handleChangeSubstance}
                                readOnly={model.readOnly}
                                stressperiods={model.stressperiods}
                            />
                        </div>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Segment>
    );
};

export default Transport;
